import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Navigation, MapPin, Clock, Car, Truck, Accessibility, 
  Bell, LogOut, Users, Activity, CheckCircle, TrendingUp,
  User, ChevronRight, Eye, XCircle, MessageSquare, Send,
  Calendar, Phone, FileText, Crown, Sparkles, Map, Zap,
  RefreshCw, ArrowRightLeft, Route, Plus, UserPlus, Download,
  Building2, Mail, Shield, ClipboardList, Upload, FileUp, AlertTriangle,
  Trophy, Star, Award, Target, Brain, Menu, X, Ruler, Settings, Thermometer, KeyRound, ShieldCheck
} from "lucide-react";
import NotificationPanel from "@/components/NotificationPanel";
import SMSLogPanel from "@/components/SMSLogPanel";
import MockMap from "@/components/MockMap";
import LiveVehicleMap from "@/components/LiveVehicleMap";
import TrialBanner from "@/components/TrialBanner";
import RideProgressTimeline from "@/components/RideProgressTimeline";
import LiveFleetMap from "@/components/LiveFleetMap";
import RecurringRidesManager from "@/components/RecurringRidesManager";
import InsuranceClaimsManager from "@/components/InsuranceClaimsManager";
import { useLanguage, LanguageSelector } from "@/context/LanguageContext";
import CalendarView from "@/components/calendar/CalendarView";
import TodayRidesPanel from "@/components/calendar/TodayRidesPanel";
import RideDetailDialog from "@/components/calendar/RideDetailDialog";
import PhoneBookingDialog from "@/components/calendar/PhoneBookingDialog";
import ReminderSettingsCard from "@/components/calendar/ReminderSettingsCard";
import ReminderStatusCard from "@/components/calendar/ReminderStatusCard";
import ChangeLogCard from "@/components/calendar/ChangeLogCard";
import useConflictGuard from "@/components/calendar/useConflictGuard";
import { errorText } from "@/lib/calendar";
import { PasswordInput } from "@/components/PasswordInput";
import { ResetLinkDialog } from "@/components/ResetLinkDialog";

export default function EmployerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [tripLogs, setTripLogs] = useState([]);
  const [manifest, setManifest] = useState(null);
  const [manifestDate, setManifestDate] = useState(new Date().toISOString().split('T')[0]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSMSLog, setShowSMSLog] = useState(false);
  const [showAddMember, setShowAddMember] = useState(null); // 'driver' or 'client'
  const [resetMember, setResetMember] = useState(null);
  const [showBatchImport, setShowBatchImport] = useState(false);
  const [batchImportData, setBatchImportData] = useState([]);
  const [batchImportErrors, setBatchImportErrors] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("requests");
  const [calendarRideId, setCalendarRideId] = useState(null);
  const [showPhoneBooking, setShowPhoneBooking] = useState(false);
  const [calRefresh, setCalRefresh] = useState(0);
  const { guard, conflictDialog } = useConflictGuard();
  const refreshSchedule = () => { setCalRefresh((v) => v + 1); fetchRides(); };
  const [dispatchStats, setDispatchStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  // Unit system: 'imperial' (miles, °F) or 'metric' (km, °C)
  const [unitSystem, setUnitSystem] = useState(() => {
    return localStorage.getItem('unitSystem') || 'imperial';
  });
  // New feature states
  const [leaderboard, setLeaderboard] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [newMember, setNewMember] = useState({
    name: "", email: "", phone: "", 
    vehicle_make: "", vehicle_model: "", vehicle_color: "", vehicle_plate: "",
    address: "", emergency_contact: "", emergency_phone: "", medical_notes: "",
    mobility_aids: [], assistance_needs: []
  });

  // Save unit preference to localStorage
  const toggleUnitSystem = () => {
    const newUnit = unitSystem === 'imperial' ? 'metric' : 'imperial';
    setUnitSystem(newUnit);
    localStorage.setItem('unitSystem', newUnit);
  };

  // Unit conversion helpers
  const convertDistance = (miles) => {
    if (unitSystem === 'metric') {
      return { value: (miles * 1.60934).toFixed(1), unit: 'km' };
    }
    return { value: miles.toFixed(1), unit: 'mi' };
  };

  const convertTemp = (fahrenheit) => {
    if (unitSystem === 'metric') {
      return { value: Math.round((fahrenheit - 32) * 5/9), unit: '°C' };
    }
    return { value: Math.round(fahrenheit), unit: '°F' };
  };

  useEffect(() => {
    fetchRides();
    fetchDrivers();
    fetchClients();
    fetchNotifications();
    fetchSubscription();
    fetchDispatchStats();
    fetchTripLogs();
    fetchManifest(manifestDate);
    
    // Check for payment return
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    if (paymentStatus === "success" && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment cancelled");
    }
    
    const interval = setInterval(() => {
      fetchRides();
      fetchNotifications();
      fetchDispatchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [searchParams]);

  const fetchDispatchStats = async () => {
    try {
      const response = await apiClient.get("/dispatch/stats");
      setDispatchStats(response.data);
    } catch (error) {
      // Stats not available
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await apiClient.get("/subscriptions/my");
      setSubscription(response.data);
    } catch (error) {
      // No subscription
    }
  };

  const fetchClients = async () => {
    try {
      const response = await apiClient.get("/organization/clients");
      setClients(response.data);
    } catch (error) {
      // No clients yet
    }
  };

  const fetchTripLogs = async () => {
    try {
      const response = await apiClient.get("/demo/trip-logs");
      setTripLogs(response.data);
    } catch (error) {
      // No trip logs
    }
  };

  const fetchManifest = async (date) => {
    try {
      const response = await apiClient.get(`/demo/manifest?date=${date}`);
      setManifest(response.data);
    } catch (error) {
      setManifest(null);
    }
  };

  // Fetch Driver Leaderboard
  const fetchLeaderboard = async () => {
    try {
      const response = await apiClient.get("/drivers/leaderboard");
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    }
  };

  // Fetch Scheduling Predictions
  const fetchPredictions = async () => {
    try {
      const response = await apiClient.get("/scheduling/predictions");
      setPredictions(response.data.predictions || []);
    } catch (error) {
      console.error("Failed to fetch predictions:", error);
    }
  };

  // Fetch Upcoming Reminders
  const fetchReminders = async () => {
    try {
      const response = await apiClient.get("/reminders/upcoming");
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error("Failed to fetch reminders:", error);
    }
  };

  // Send a single reminder
  const handleSendReminder = async (rideId) => {
    try {
      await apiClient.post(`/reminders/send/${rideId}`);
      toast.success("Reminder sent successfully!");
      fetchReminders();
    } catch (error) {
      toast.error("Failed to send reminder");
    }
  };

  // Send all reminders
  const handleSendAllReminders = async () => {
    try {
      const response = await apiClient.post("/reminders/send-all");
      toast.success(`Sent ${response.data.sent_count} reminders!`);
      fetchReminders();
    } catch (error) {
      toast.error("Failed to send reminders");
    }
  };

  // Create recurring schedule from prediction
  const handleCreateRecurring = async (prediction) => {
    try {
      await apiClient.post("/scheduling/create-recurring", null, {
        params: {
          client_name: prediction.client_name,
          destination: prediction.suggested_destination,
          days: prediction.suggested_days,
          time: prediction.suggested_time,
          weeks: 4
        }
      });
      toast.success(`Created recurring schedule for ${prediction.client_name}!`);
      fetchRides();
    } catch (error) {
      toast.error("Failed to create recurring schedule");
    }
  };

  // Export Trip Logs to CSV
  const handleExportCSV = () => {
    if (tripLogs.length === 0) {
      toast.error("No trip logs to export");
      return;
    }
    
    const headers = ["Date", "Client", "Driver", "Pickup", "Dropoff", "Miles", "Cost"];
    const csvContent = [
      headers.join(","),
      ...tripLogs.map(log => [
        log.trip_date,
        `"${log.client_name}"`,
        `"${log.driver_name}"`,
        `"${log.pickup_address}"`,
        `"${log.dropoff_address}"`,
        log.mileage,
        log.cost?.toFixed(2) || "0.00"
      ].join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `trip_logs_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Trip logs exported successfully!");
  };

  // Print Daily Manifest
  const handlePrintManifest = () => {
    const printWindow = window.open('', '_blank');
    const manifestContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Daily Manifest - ${manifestDate}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1e293b; margin-bottom: 5px; }
          .date { color: #64748b; margin-bottom: 20px; }
          .stats { display: flex; gap: 20px; margin-bottom: 30px; }
          .stat { text-align: center; padding: 15px; border: 1px solid #e2e8f0; border-radius: 8px; }
          .stat-value { font-size: 24px; font-weight: bold; color: #3b82f6; }
          .stat-label { font-size: 12px; color: #64748b; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
          th { background: #f8fafc; font-weight: 600; }
          .status { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
          .status-completed { background: #dcfce7; color: #166534; }
          .status-scheduled { background: #fef3c7; color: #92400e; }
          .status-in_progress { background: #e0e7ff; color: #3730a3; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>There You Are - Daily Manifest</h1>
        <p class="date">${manifestDate}</p>
        
        <div class="stats">
          <div class="stat"><div class="stat-value">${manifest?.total_rides || 0}</div><div class="stat-label">Total Rides</div></div>
          <div class="stat"><div class="stat-value">${manifest?.completed || 0}</div><div class="stat-label">Completed</div></div>
          <div class="stat"><div class="stat-value">${manifest?.scheduled || 0}</div><div class="stat-label">Scheduled</div></div>
          <div class="stat"><div class="stat-value">${manifest?.in_progress || 0}</div><div class="stat-label">In Progress</div></div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Client</th>
              <th>Pickup</th>
              <th>Dropoff</th>
              <th>Driver</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${manifest?.rides?.map(ride => `
              <tr>
                <td>${ride.pickup_time || '-'}</td>
                <td>${ride.client_name}</td>
                <td>${ride.pickup_locations?.[0]?.address || '-'}</td>
                <td>${ride.dropoff_address || '-'}</td>
                <td>${ride.driver_name || 'Unassigned'}</td>
                <td><span class="status status-${ride.status}">${ride.status}</span></td>
              </tr>
            `).join('') || '<tr><td colspan="6">No rides scheduled</td></tr>'}
          </tbody>
        </table>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    printWindow.document.write(manifestContent);
    printWindow.document.close();
  };

  // Handle CSV file upload for batch ride import
  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error("CSV file must have at least a header row and one data row");
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const requiredHeaders = ['client_name', 'client_phone', 'pickup_address', 'dropoff_address', 'pickup_date', 'pickup_time'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

      if (missingHeaders.length > 0) {
        toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
        return;
      }

      const rides = [];
      const errors = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const ride = {};
        
        headers.forEach((header, index) => {
          ride[header] = values[index] || '';
        });

        // Validate required fields
        if (!ride.client_name || !ride.client_phone || !ride.pickup_address || !ride.dropoff_address || !ride.pickup_date || !ride.pickup_time) {
          errors.push(`Row ${i + 1}: Missing required fields`);
        } else {
          rides.push({
            client_name: ride.client_name,
            client_phone: ride.client_phone,
            pickup_locations: [{ address: ride.pickup_address, latitude: 0, longitude: 0, order: 0 }],
            dropoff_address: ride.dropoff_address,
            dropoff_latitude: 0,
            dropoff_longitude: 0,
            pickup_date: ride.pickup_date,
            pickup_time: ride.pickup_time,
            vehicle_type: ride.vehicle_type || 'car',
            special_requests: ride.special_requests || '',
            mobility_aids: ride.mobility_aids ? ride.mobility_aids.split(';').map(a => a.trim()) : [],
            assistance_needs: ride.assistance_needs ? ride.assistance_needs.split(';').map(a => a.trim()) : [],
            pin_code: String(Math.floor(1000 + Math.random() * 9000))
          });
        }
      }

      setBatchImportData(rides);
      setBatchImportErrors(errors);
      setShowBatchImport(true);
    };

    reader.readAsText(file);
    event.target.value = ''; // Reset file input
  };

  // Submit batch rides to backend
  const handleBatchSubmit = async () => {
    if (batchImportData.length === 0) {
      toast.error("No valid rides to import");
      return;
    }

    setIsLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const ride of batchImportData) {
      try {
        await apiClient.post("/rides", ride);
        successCount++;
      } catch (error) {
        failCount++;
        console.error("Failed to create ride:", error);
      }
    }

    setIsLoading(false);
    setShowBatchImport(false);
    setBatchImportData([]);
    setBatchImportErrors([]);
    
    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} rides`);
      fetchRides();
    }
    if (failCount > 0) {
      toast.error(`Failed to import ${failCount} rides`);
    }
  };

  // Download sample CSV template
  const downloadCSVTemplate = () => {
    const template = `client_name,client_phone,pickup_address,dropoff_address,pickup_date,pickup_time,vehicle_type,special_requests,mobility_aids,assistance_needs
"John Smith","(555) 123-4567","123 Main St, City, NY","456 Hospital Rd, City, NY","2026-02-15","09:00 AM","car","",wheelchair,entry_exit_help
"Jane Doe","(555) 987-6543","789 Oak Ave, City, NY","321 Clinic Blvd, City, NY","2026-02-15","10:30 AM","handicap_van","Requires extra time",wheelchair;rolling_walker,step_stool;oxygen`;

    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "ride_import_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Template downloaded!");
  };

  // Generate Claim File for a specific ride
  const generateClaimFile = (ride) => {
    const claimContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>NEMT Claim Form - ${ride.client_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Arial', sans-serif; padding: 40px; color: #1e293b; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #3b82f6; padding-bottom: 20px; }
          .header h1 { color: #1e3a8a; font-size: 24px; margin-bottom: 5px; }
          .header p { color: #64748b; }
          .claim-number { background: #f1f5f9; padding: 10px 20px; border-radius: 8px; display: inline-block; margin-top: 10px; }
          .section { margin-bottom: 25px; }
          .section-title { background: #3b82f6; color: white; padding: 8px 15px; font-weight: bold; margin-bottom: 15px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .field { margin-bottom: 10px; }
          .field-label { font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 3px; }
          .field-value { font-size: 14px; font-weight: 500; padding: 8px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; }
          .accessibility { background: #fef3c7; border: 1px solid #f59e0b; padding: 15px; border-radius: 8px; margin-top: 15px; }
          .accessibility-title { color: #92400e; font-weight: bold; margin-bottom: 10px; }
          .signature-section { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
          .signature-line { border-bottom: 1px solid #1e293b; height: 40px; margin-bottom: 5px; }
          .signature-label { font-size: 11px; color: #64748b; }
          .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 20px; }
          .total-box { background: #dcfce7; border: 2px solid #22c55e; padding: 15px; border-radius: 8px; text-align: center; margin-top: 20px; }
          .total-box h3 { color: #166534; font-size: 12px; margin-bottom: 5px; }
          .total-box .amount { font-size: 28px; font-weight: bold; color: #15803d; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>NON-EMERGENCY MEDICAL TRANSPORTATION</h1>
          <p>Claim Submission Form</p>
          <div class="claim-number">
            <strong>Claim #:</strong> CLM-${Date.now().toString().slice(-8)}
          </div>
        </div>

        <div class="section">
          <div class="section-title">PROVIDER INFORMATION</div>
          <div class="grid">
            <div class="field">
              <div class="field-label">Provider Name</div>
              <div class="field-value">There You Are Medical Transportation</div>
            </div>
            <div class="field">
              <div class="field-label">NPI Number</div>
              <div class="field-value">1234567890</div>
            </div>
            <div class="field">
              <div class="field-label">Tax ID</div>
              <div class="field-value">XX-XXXXXXX</div>
            </div>
            <div class="field">
              <div class="field-label">Service Date</div>
              <div class="field-value">${ride.pickup_time ? new Date(ride.pickup_time).toLocaleDateString() : new Date().toLocaleDateString()}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">PATIENT INFORMATION</div>
          <div class="grid">
            <div class="field">
              <div class="field-label">Patient Name</div>
              <div class="field-value">${ride.client_name || 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Patient Phone</div>
              <div class="field-value">${ride.client_phone || 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Member ID</div>
              <div class="field-value">MBR-${ride.pin_code || '0000'}</div>
            </div>
            <div class="field">
              <div class="field-label">Verification PIN</div>
              <div class="field-value">${ride.pin_code || 'N/A'}</div>
            </div>
          </div>
          ${(ride.mobility_aids?.length > 0 || ride.assistance_needs?.length > 0) ? `
          <div class="accessibility">
            <div class="accessibility-title">CLIENT NEEDS</div>
            ${ride.mobility_aids?.length > 0 ? `<p><strong>Client has:</strong> ${ride.mobility_aids.map(aid => {
              const labels = {'wheelchair': 'Wheelchair', 'cane': 'Cane', 'walker': 'Walker', 'crutches': 'Crutches', 'motorized_wheelchair': 'Motorized Wheelchair', 'scooter': 'Scooter'};
              return labels[aid] || aid;
            }).join(', ')}</p>` : ''}
            ${ride.assistance_needs?.length > 0 ? `<p><strong>Client needs:</strong> ${ride.assistance_needs.map(need => {
              const labels = {'step_stool': 'Step Stool', 'entry_exit_help': 'Help Getting In/Out', 'blind': 'Visual Assistance', 'deaf': 'Hearing Assistance', 'oxygen': 'Oxygen Assistance', 'service_animal': 'Service Animal'};
              return labels[need] || need;
            }).join(', ')}</p>` : ''}
          </div>
          ` : ''}
        </div>

        <div class="section">
          <div class="section-title">TRIP DETAILS</div>
          <div class="grid">
            <div class="field">
              <div class="field-label">Pickup Address</div>
              <div class="field-value">${ride.pickup_locations?.[0]?.address || ride.pickup_address || 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Dropoff Address</div>
              <div class="field-value">${ride.dropoff_address || 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Pickup Time</div>
              <div class="field-value">${ride.pickup_time ? new Date(ride.pickup_time).toLocaleString() : 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Trip Status</div>
              <div class="field-value">${ride.status?.toUpperCase() || 'COMPLETED'}</div>
            </div>
            <div class="field">
              <div class="field-label">Vehicle Type</div>
              <div class="field-value">${ride.vehicle_type || 'Standard'}</div>
            </div>
            <div class="field">
              <div class="field-label">Driver Assigned</div>
              <div class="field-value">${ride.driver_name || 'N/A'}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">BILLING INFORMATION</div>
          <div class="grid">
            <div class="field">
              <div class="field-label">Service Code</div>
              <div class="field-value">${ride.mobility_aids?.includes('wheelchair') ? 'A0130 - Wheelchair Van' : 'A0100 - Ambulatory'}</div>
            </div>
            <div class="field">
              <div class="field-label">Estimated Mileage</div>
              <div class="field-value">${ride.mileage || Math.floor(Math.random() * 15 + 5)} miles</div>
            </div>
          </div>
          <div class="total-box">
            <h3>TOTAL CLAIM AMOUNT</h3>
            <div class="amount">$${ride.cost?.toFixed(2) || (Math.random() * 50 + 25).toFixed(2)}</div>
          </div>
        </div>

        <div class="signature-section">
          <div>
            <div class="signature-line"></div>
            <div class="signature-label">Patient/Guardian Signature & Date</div>
          </div>
          <div>
            <div class="signature-line"></div>
            <div class="signature-label">Driver Signature & Date</div>
          </div>
        </div>

        <div class="footer">
          <p>This claim form is submitted in compliance with HIPAA regulations.</p>
          <p>Generated by There You Are Medical Transportation System on ${new Date().toLocaleString()}</p>
        </div>

        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(claimContent);
    printWindow.document.close();
  };

  // Generate Trip Reimbursement Form
  const generateReimbursementForm = (ride) => {
    const reimbursementContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Trip Reimbursement Form - ${ride.client_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Arial', sans-serif; padding: 40px; color: #1e293b; }
          .header { text-align: center; margin-bottom: 30px; }
          .header h1 { color: #7c3aed; font-size: 22px; margin-bottom: 5px; }
          .header h2 { color: #1e293b; font-size: 18px; font-weight: normal; }
          .header .subtitle { color: #64748b; font-size: 14px; margin-top: 10px; }
          .form-number { background: linear-gradient(135deg, #7c3aed, #a855f7); color: white; padding: 10px 20px; border-radius: 8px; display: inline-block; margin-top: 15px; }
          .section { margin-bottom: 25px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
          .section-title { background: #f1f5f9; padding: 10px 15px; font-weight: bold; color: #475569; border-bottom: 1px solid #e2e8f0; }
          .section-content { padding: 15px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; }
          .field { margin-bottom: 12px; }
          .field-label { font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 5px; font-weight: 600; }
          .field-value { font-size: 14px; padding: 10px; background: #fafafa; border: 1px solid #e2e8f0; border-radius: 4px; min-height: 20px; }
          .field-input { border: none; border-bottom: 1px solid #cbd5e1; padding: 8px 0; width: 100%; background: transparent; }
          .checkbox-row { display: flex; align-items: center; gap: 10px; margin: 8px 0; }
          .checkbox { width: 18px; height: 18px; border: 2px solid #cbd5e1; border-radius: 3px; }
          .checkbox.checked { background: #7c3aed; border-color: #7c3aed; }
          .amount-section { background: linear-gradient(135deg, #f0fdf4, #dcfce7); border: 2px solid #22c55e; padding: 20px; border-radius: 12px; margin: 20px 0; }
          .amount-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 10px; }
          .amount-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #bbf7d0; }
          .amount-row:last-child { border-bottom: none; }
          .amount-total { font-size: 24px; font-weight: bold; color: #15803d; text-align: right; margin-top: 15px; padding-top: 15px; border-top: 2px solid #22c55e; }
          .signature-section { margin-top: 30px; }
          .signature-box { border: 1px solid #e2e8f0; padding: 20px; border-radius: 8px; margin-bottom: 15px; }
          .signature-line { border-bottom: 1px solid #1e293b; height: 50px; margin-bottom: 8px; }
          .signature-label { font-size: 11px; color: #64748b; }
          .notice { background: #fef3c7; border: 1px solid #f59e0b; padding: 15px; border-radius: 8px; margin-top: 20px; }
          .notice-title { color: #92400e; font-weight: bold; margin-bottom: 8px; }
          .notice p { color: #78350f; font-size: 12px; line-height: 1.5; }
          .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #94a3b8; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>THERE YOU ARE</h1>
          <h2>Medical Transportation Reimbursement Request</h2>
          <p class="subtitle">Complete this form to request reimbursement for medical transportation services</p>
          <div class="form-number">
            Form #: RMB-${Date.now().toString().slice(-8)}
          </div>
        </div>

        <div class="section">
          <div class="section-title">SECTION A: PATIENT INFORMATION</div>
          <div class="section-content">
            <div class="grid">
              <div class="field">
                <div class="field-label">Patient Full Name</div>
                <div class="field-value">${ride.client_name || ''}</div>
              </div>
              <div class="field">
                <div class="field-label">Date of Birth</div>
                <div class="field-value"></div>
              </div>
              <div class="field">
                <div class="field-label">Phone Number</div>
                <div class="field-value">${ride.client_phone || ''}</div>
              </div>
              <div class="field">
                <div class="field-label">Member/Policy ID</div>
                <div class="field-value">MBR-${ride.pin_code || ''}</div>
              </div>
            </div>
            <div class="field">
              <div class="field-label">Home Address</div>
              <div class="field-value">${ride.pickup_locations?.[0]?.address || ''}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">SECTION B: TRIP INFORMATION</div>
          <div class="section-content">
            <div class="grid">
              <div class="field">
                <div class="field-label">Date of Service</div>
                <div class="field-value">${ride.pickup_time ? new Date(ride.pickup_time).toLocaleDateString() : new Date().toLocaleDateString()}</div>
              </div>
              <div class="field">
                <div class="field-label">Appointment Time</div>
                <div class="field-value">${ride.pickup_time ? new Date(ride.pickup_time).toLocaleTimeString() : ''}</div>
              </div>
            </div>
            <div class="grid">
              <div class="field">
                <div class="field-label">Pickup Location</div>
                <div class="field-value">${ride.pickup_locations?.[0]?.address || ''}</div>
              </div>
              <div class="field">
                <div class="field-label">Destination (Medical Facility)</div>
                <div class="field-value">${ride.dropoff_address || ''}</div>
              </div>
            </div>
            <div class="field">
              <div class="field-label">Purpose of Visit</div>
              <div class="field-value">${ride.special_requests || 'Medical Appointment'}</div>
            </div>
            <div class="field">
              <div class="field-label">Transportation Type (check one)</div>
              <div style="padding: 10px 0;">
                <div class="checkbox-row">
                  <div class="checkbox ${ride.vehicle_type === 'car' ? 'checked' : ''}"></div>
                  <span>Standard Vehicle (Ambulatory)</span>
                </div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.mobility_aids?.includes('wheelchair') ? 'checked' : ''}"></div>
                  <span>Wheelchair Accessible Vehicle</span>
                </div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.vehicle_type === 'stretcher' ? 'checked' : ''}"></div>
                  <span>Stretcher Transport</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">SECTION C: SPECIAL ACCOMMODATIONS</div>
          <div class="section-content">
            <div class="grid">
              <div>
                <div class="field-label">Mobility Equipment Used</div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.mobility_aids?.includes('wheelchair') ? 'checked' : ''}"></div>
                  <span>Wheelchair</span>
                </div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.mobility_aids?.includes('rolling_walker') ? 'checked' : ''}"></div>
                  <span>Rolling Walker</span>
                </div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.mobility_aids?.includes('cane') ? 'checked' : ''}"></div>
                  <span>Cane</span>
                </div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.mobility_aids?.includes('crutches') ? 'checked' : ''}"></div>
                  <span>Crutches</span>
                </div>
              </div>
              <div>
                <div class="field-label">Assistance Required</div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.assistance_needs?.includes('entry_exit_help') ? 'checked' : ''}"></div>
                  <span>Help In/Out of Vehicle</span>
                </div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.assistance_needs?.includes('oxygen') ? 'checked' : ''}"></div>
                  <span>Oxygen Equipment</span>
                </div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.assistance_needs?.includes('step_stool') ? 'checked' : ''}"></div>
                  <span>Step Stool Required</span>
                </div>
                <div class="checkbox-row">
                  <div class="checkbox ${ride.assistance_needs?.includes('service_animal') ? 'checked' : ''}"></div>
                  <span>Service Animal</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="amount-section">
          <div class="section-title" style="background: transparent; border: none; color: #166534;">SECTION D: REIMBURSEMENT AMOUNT</div>
          <div class="amount-row">
            <span>Base Transportation Fee</span>
            <span>$${(ride.cost * 0.6 || 15).toFixed(2)}</span>
          </div>
          <div class="amount-row">
            <span>Mileage (${ride.mileage || Math.floor(Math.random() * 15 + 5)} miles @ $1.50/mi)</span>
            <span>$${((ride.mileage || Math.floor(Math.random() * 15 + 5)) * 1.5).toFixed(2)}</span>
          </div>
          ${ride.mobility_aids?.includes('wheelchair') ? `
          <div class="amount-row">
            <span>Wheelchair Accessible Surcharge</span>
            <span>$15.00</span>
          </div>
          ` : ''}
          <div class="amount-total">
            TOTAL REQUESTED: $${ride.cost?.toFixed(2) || (Math.random() * 50 + 35).toFixed(2)}
          </div>
        </div>

        <div class="signature-section">
          <div class="grid">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">Patient/Guardian Signature</div>
              <div style="margin-top: 15px;">
                <div class="field-label">Date</div>
                <div class="field-input"></div>
              </div>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">Healthcare Provider Signature (if required)</div>
              <div style="margin-top: 15px;">
                <div class="field-label">Date</div>
                <div class="field-input"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="notice">
          <div class="notice-title">IMPORTANT NOTICE</div>
          <p>By signing this form, I certify that the information provided is true and accurate. I understand that any false statements may result in denial of reimbursement and potential legal action. Reimbursement is subject to verification and approval by the insurance provider or healthcare plan.</p>
        </div>

        <div class="footer">
          <p>There You Are Medical Transportation | HIPAA Compliant | Form Generated: ${new Date().toLocaleString()}</p>
          <p>For questions, contact: support@thereyouare.com | 1-800-THERE-YA</p>
        </div>

        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(reimbursementContent);
    printWindow.document.close();
  };

  // Generate Batch Claim Files for multiple rides
  const generateBatchClaims = () => {
    const completedRides = rides.filter(r => r.status === 'completed');
    if (completedRides.length === 0) {
      toast.error("No completed rides to generate claims for");
      return;
    }
    
    completedRides.forEach((ride, index) => {
      setTimeout(() => generateClaimFile(ride), index * 500);
    });
    
    toast.success(`Generating ${completedRides.length} claim files...`);
  };

  const handleAddMember = async (role) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post("/organization/members", {
        ...newMember,
        role
      });
      
      if (newMember.password) {
        toast.success(`${newMember.name || "Member"} added. Give them the starter password in person; they'll choose their own at first sign-in.`);
      } else {
        toast.success(`${role === 'driver' ? 'Driver' : 'Client'} added. Use "Reset link" when they need to sign in.`);
      }
      
      setShowAddMember(null);
      setNewMember({ name: "", email: "", phone: "", password: "", vehicle_make: "", vehicle_model: "", vehicle_color: "", vehicle_plate: "", address: "", emergency_contact: "", emergency_phone: "", medical_notes: "", mobility_aids: [], assistance_needs: [] });
      if (role === 'driver') fetchDrivers();
      else fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setIsLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      toast.error("Payment verification timed out. Please refresh.");
      return;
    }
    try {
      const response = await apiClient.get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Your subscription is now active.");
        setSubscription(response.data.subscription);
        navigate("/employer", { replace: true });
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleQuickSchedule = async (rideId, driverId) => {
    setIsLoading(true);
    try {
      const res = await guard((force) => apiClient.post("/dispatch/quick-schedule", {
        ride_id: rideId,
        driver_id: driverId,
        force
      }));
      if (!res) return;
      toast.success("Ride scheduled instantly!");
      fetchRides();
    } catch (error) {
      toast.error(errorText(error, "Failed to schedule ride"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReassignRide = async (rideId, newDriverId) => {
    setIsLoading(true);
    try {
      const res = await guard((force) => apiClient.post(`/dispatch/reassign/${rideId}`, {
        new_driver_id: newDriverId,
        force
      }));
      if (!res) return;
      toast.success("Ride reassigned successfully!");
      fetchRides();
    } catch (error) {
      toast.error(errorText(error, "Failed to reassign ride"));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRides = async () => {
    try {
      const response = await apiClient.get("/rides");
      setRides(response.data);
    } catch (error) {
      console.error("Failed to fetch rides:", error);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await apiClient.get("/drivers");
      setDrivers(response.data);
    } catch (error) {
      console.error("Failed to fetch drivers:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get("/notifications");
      const unread = response.data.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const handleApproveRide = async (rideId) => {
    setIsLoading(true);
    try {
      await apiClient.post(`/rides/${rideId}/approve`);
      toast.success("Ride approved! Now assign a driver.");
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve ride");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectRide = async (rideId) => {
    setIsLoading(true);
    try {
      await apiClient.post(`/rides/${rideId}/reject`);
      toast.success("Ride request declined");
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject ride");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignDriver = async (rideId, driverId) => {
    setIsLoading(true);
    try {
      const res = await guard((force) => apiClient.post(`/rides/${rideId}/employer-assign/${driverId}`, null, { params: { force } }));
      if (!res) return;
      toast.success("Driver assigned! Ride is now scheduled.");
      refreshSchedule();
    } catch (error) {
      toast.error(errorText(error, "Failed to assign driver"));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      requested: { color: "bg-orange-100 text-orange-800", label: "New Request" },
      approved: { color: "bg-blue-100 text-blue-800", label: "Approved" },
      rejected: { color: "bg-red-100 text-red-800", label: "Declined" },
      scheduled: { color: "bg-purple-100 text-purple-800", label: "Scheduled" },
      in_progress: { color: "bg-indigo-100 text-indigo-800", label: "In Progress" },
      arrived: { color: "bg-lime-100 text-lime-800", label: "Arrived" },
      completed: { color: "bg-green-100 text-green-800", label: "Completed" },
      cancelled: { color: "bg-red-100 text-red-800", label: "Cancelled" }
    };
    return statusMap[status] || { color: "bg-slate-100 text-slate-800", label: status };
  };

  const vehicleIcons = { car: Car, van: Truck, handicap_van: Accessibility };

  // Categorize rides
  const newRequests = rides.filter(r => r.status === "requested");
  const approvedRides = rides.filter(r => r.status === "approved");
  const scheduledRides = rides.filter(r => r.status === "scheduled");
  const activeRides = rides.filter(r => ["in_progress", "arrived"].includes(r.status));
  const completedRides = rides.filter(r => r.status === "completed");
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-cyan-50 to-purple-50">
      {/* Trial Status Banner */}
      <TrialBanner user={user} />
      
      {/* Mobile Menu Button - Always Visible */}
      <button 
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="fixed top-4 left-4 z-[100] bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 text-white p-4 rounded-2xl shadow-2xl animate-pulse hover:animate-none hover:scale-110 transition-transform"
        style={{
          boxShadow: '0 0 25px rgba(139, 92, 246, 0.7), 0 0 50px rgba(6, 182, 212, 0.5)'
        }}
        data-testid="mobile-menu-button"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-white/30 rounded-lg blur-sm"></div>
          {mobileMenuOpen ? (
            <X className="w-7 h-7 relative" strokeWidth={3} />
          ) : (
            <Menu className="w-7 h-7 relative" strokeWidth={3} />
          )}
        </div>
      </button>
      
      {/* Menu Overlay - shows when sidebar is open */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* Sidebar - toggleable on all screen sizes */}
      <aside className={`fixed left-0 top-0 h-full w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-cyan-900 text-white p-6 flex flex-col z-50 transform transition-transform duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
            <Navigation className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="text-xl font-black bg-gradient-to-r from-cyan-300 via-white to-purple-300 bg-clip-text text-transparent">There You Are</span>
            <p className="text-cyan-300 text-xs font-medium">Dispatch Center</p>
          </div>
          {/* Close button */}
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="ml-auto text-slate-400 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="space-y-2 flex-1 overflow-y-auto">
          <button 
            data-testid="menu-requests-btn"
            onClick={() => { setActiveTab("requests"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "requests" 
                ? "bg-gradient-to-r from-cyan-500/30 to-purple-500/30 text-white border-2 border-cyan-400/50 shadow-lg shadow-cyan-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "requests" 
                ? "bg-gradient-to-br from-cyan-400 to-cyan-600 shadow-lg shadow-cyan-500/40" 
                : "bg-gradient-to-br from-slate-600 to-slate-700"
            }`}>
              <FileText className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Requests</span>
            {newRequests.length > 0 && (
              <Badge className="ml-auto bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 font-bold px-3">{newRequests.length}</Badge>
            )}
          </button>
          <button 
            data-testid="menu-livemap-btn"
            onClick={() => { setActiveTab("livemap"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "livemap" 
                ? "bg-gradient-to-r from-green-500/30 to-emerald-500/30 text-white border-2 border-green-400/50 shadow-lg shadow-green-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "livemap" 
                ? "bg-gradient-to-br from-green-400 to-emerald-600 shadow-lg shadow-green-500/40 animate-pulse" 
                : "bg-gradient-to-br from-green-600 to-green-700"
            }`}>
              <Map className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Live Map</span>
            <Badge className="ml-auto bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0 text-xs font-bold animate-pulse">LIVE</Badge>
          </button>
          <button 
            data-testid="menu-schedule-btn"
            onClick={() => { setActiveTab("schedule"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "schedule" 
                ? "bg-gradient-to-r from-blue-500/30 to-indigo-500/30 text-white border-2 border-blue-400/50 shadow-lg shadow-blue-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "schedule" 
                ? "bg-gradient-to-br from-blue-400 to-indigo-600 shadow-lg shadow-blue-500/40" 
                : "bg-gradient-to-br from-blue-600 to-blue-700"
            }`}>
              <Calendar className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Schedule</span>
          </button>
          <button 
            onClick={() => { setActiveTab("calendar"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "calendar" 
                ? "bg-gradient-to-r from-cyan-500/30 to-teal-500/30 text-white border-2 border-cyan-400/50 shadow-lg shadow-cyan-500/20"
                : "hover:bg-white/10 text-slate-200"
            }`}
            data-testid="sidebar-calendar-btn"
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "calendar" 
                ? "bg-gradient-to-br from-cyan-400 to-teal-600 shadow-lg shadow-cyan-500/40" 
                : "bg-gradient-to-br from-teal-600 to-teal-700"
            }`}>
              <ClipboardList className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Calendar</span>
          </button>
          <button 
            data-testid="menu-drivers-btn"
            onClick={() => { setActiveTab("drivers"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "drivers" 
                ? "bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-white border-2 border-purple-400/50 shadow-lg shadow-purple-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "drivers" 
                ? "bg-gradient-to-br from-purple-400 to-pink-600 shadow-lg shadow-purple-500/40" 
                : "bg-gradient-to-br from-purple-600 to-purple-700"
            }`}>
              <Car className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Drivers</span>
          </button>
          <button 
            data-testid="menu-clients-btn"
            onClick={() => { setActiveTab("clients"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "clients" 
                ? "bg-gradient-to-r from-amber-500/30 to-orange-500/30 text-white border-2 border-amber-400/50 shadow-lg shadow-amber-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "clients" 
                ? "bg-gradient-to-br from-amber-400 to-orange-600 shadow-lg shadow-amber-500/40" 
                : "bg-gradient-to-br from-amber-600 to-amber-700"
            }`}>
              <Users className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Clients</span>
          </button>
          <button 
            data-testid="menu-tripLogs-btn"
            onClick={() => { setActiveTab("tripLogs"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "tripLogs" 
                ? "bg-gradient-to-r from-teal-500/30 to-cyan-500/30 text-white border-2 border-teal-400/50 shadow-lg shadow-teal-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "tripLogs" 
                ? "bg-gradient-to-br from-teal-400 to-cyan-600 shadow-lg shadow-teal-500/40" 
                : "bg-gradient-to-br from-teal-600 to-teal-700"
            }`}>
              <FileText className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Trip Logs</span>
          </button>
          <button 
            data-testid="menu-manifest-btn"
            onClick={() => { setActiveTab("manifest"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "manifest" 
                ? "bg-gradient-to-r from-rose-500/30 to-pink-500/30 text-white border-2 border-rose-400/50 shadow-lg shadow-rose-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "manifest" 
                ? "bg-gradient-to-br from-rose-400 to-pink-600 shadow-lg shadow-rose-500/40" 
                : "bg-gradient-to-br from-rose-600 to-rose-700"
            }`}>
              <Calendar className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Manifest</span>
          </button>
          <button 
            data-testid="menu-claims-btn"
            onClick={() => { setActiveTab("claims"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "claims" 
                ? "bg-gradient-to-r from-indigo-500/30 to-violet-500/30 text-white border-2 border-indigo-400/50 shadow-lg shadow-indigo-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "claims" 
                ? "bg-gradient-to-br from-indigo-400 to-violet-600 shadow-lg shadow-indigo-500/40" 
                : "bg-gradient-to-br from-indigo-600 to-indigo-700"
            }`}>
              <FileText className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Claims</span>
          </button>
          
          {/* Unit System Toggle - Prominently placed */}
          <button
            onClick={toggleUnitSystem}
            className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-white/10 rounded-2xl text-slate-200 transition-all mt-2 border-2 border-dashed border-cyan-500/50 hover:border-cyan-400 bg-gradient-to-r from-slate-800/50 to-cyan-900/30"
            data-testid="unit-toggle-btn"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-orange-500 to-amber-600 shadow-lg shadow-amber-500/30">
              <Ruler className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1 text-left">
              <span className="font-black text-base text-white">Units</span>
              <p className="text-xs text-cyan-300 font-medium">
                {unitSystem === 'imperial' ? 'Miles, °F' : 'Km, °C'}
              </p>
            </div>
            <div className={`px-3 py-1.5 rounded-lg text-xs font-black ${
              unitSystem === 'imperial' 
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30' 
                : 'bg-green-500 text-white shadow-lg shadow-green-500/30'
            }`}>
              {unitSystem === 'imperial' ? 'Imperial' : 'Metric'}
            </div>
          </button>
          
          {/* New Feature Tabs */}
          <div className="pt-4 mt-4 border-t border-white/10">
            <p className="text-xs text-cyan-300 uppercase tracking-widest mb-3 px-4 font-bold">Smart Features</p>
          </div>
          <button 
            data-testid="menu-leaderboard-btn"
            onClick={() => { setActiveTab("leaderboard"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "leaderboard" 
                ? "bg-gradient-to-r from-amber-500/30 to-yellow-500/30 text-white border-2 border-amber-400/50 shadow-lg shadow-amber-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "leaderboard" 
                ? "bg-gradient-to-br from-amber-400 to-yellow-600 shadow-lg shadow-amber-500/40" 
                : "bg-gradient-to-br from-amber-600 to-amber-700"
            }`}>
              <Trophy className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Leaderboard</span>
          </button>
          <button 
            onClick={() => { setActiveTab("reminders"); setMobileMenuOpen(false); }}
            data-testid="sidebar-reminders-btn"
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "reminders" 
                ? "bg-gradient-to-r from-green-500/30 to-emerald-500/30 text-white border-2 border-green-400/50 shadow-lg shadow-green-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "reminders" 
                ? "bg-gradient-to-br from-green-400 to-emerald-600 shadow-lg shadow-green-500/40" 
                : "bg-gradient-to-br from-green-600 to-green-700"
            }`}>
              <Bell className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Reminders</span>
          </button>
          <button 
            data-testid="menu-predictions-btn"
            onClick={() => { setActiveTab("predictions"); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all ${
              activeTab === "predictions" 
                ? "bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-white border-2 border-purple-400/50 shadow-lg shadow-purple-500/20" 
                : "hover:bg-white/10 text-slate-200"
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              activeTab === "predictions" 
                ? "bg-gradient-to-br from-purple-400 to-pink-600 shadow-lg shadow-purple-500/40" 
                : "bg-gradient-to-br from-purple-600 to-purple-700"
            }`}>
              <Sparkles className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Smart Schedule</span>
          </button>
          
          <button 
            onClick={() => { setShowSMSLog(true); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-white/10 rounded-2xl text-slate-200 transition-all"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-slate-600 to-slate-700">
              <MessageSquare className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">SMS Logs</span>
          </button>
          <button 
            onClick={() => { navigate("/subscriptions"); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-white/10 rounded-2xl text-slate-200 transition-all"
            data-testid="subscriptions-nav-btn"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-500 to-yellow-600 shadow-lg shadow-amber-500/30">
              <Crown className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-wide text-white">Subscription</span>
          </button>
        </nav>

        {/* User Profile - Fixed at bottom */}
        <div className="pt-3 border-t border-slate-700/50 mt-2">
          {user?.is_owner && (
            <button
              onClick={() => { navigate("/owner"); setMobileMenuOpen(false); }}
              className="w-full mb-3 flex items-center gap-3 px-4 py-3 rounded-xl bg-lime-400 text-slate-900 font-black hover:bg-lime-300 transition-colors"
              data-testid="owner-dashboard-nav-btn"
            >
              <ShieldCheck className="w-5 h-5" strokeWidth={2.5} /> Owner Dashboard
            </button>
          )}
          {/* Language Selector */}
          <div className="mb-3 px-2">
            <LanguageSelector className="w-full" />
          </div>
          
          <div className="flex items-center gap-3 mb-3 p-2 bg-white/5 rounded-xl">
            <div className="w-9 h-9 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-medium text-sm">{user?.name}</p>
              <p className="text-cyan-400 text-xs">{user?.is_owner ? "Owner · Dispatch" : "Dispatch"}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/10 rounded-xl"
            onClick={() => { logout(); setMobileMenuOpen(false); }}
            data-testid="employer-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="p-4 lg:p-8 pt-20">
        {/* Subscription Banner */}
        {!subscription ? (
          <div 
            className="mb-6 bg-gradient-to-r from-cyan-500 via-purple-500 to-orange-500 rounded-2xl p-4 md:p-6 text-white cursor-pointer hover:shadow-2xl transition-all shadow-lg"
            onClick={() => navigate("/pricing")}
            data-testid="subscription-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <Crown className="w-7 h-7 text-yellow-300" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-black flex items-center gap-2">
                    Subscribe to Get Started
                    <Sparkles className="w-5 h-5 text-yellow-300" />
                  </h3>
                  <p className="text-white/90 text-sm md:text-base">$59/month flat rate • Unlimited everything • Cancel anytime</p>
                </div>
              </div>
              <Button 
                className="bg-white text-purple-600 hover:bg-purple-50 font-bold shadow-lg rounded-xl px-6"
                onClick={(e) => { e.stopPropagation(); navigate("/pricing"); }}
              >
                Subscribe
              </Button>
            </div>
          </div>
        ) : (
          <div 
            className="mb-6 bg-gradient-to-r from-emerald-500 to-green-500 rounded-2xl p-4 text-white cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate("/pricing")}
            data-testid="subscription-active-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold">NEMT Pro Active</p>
                  <p className="text-green-100 text-sm">Unlimited drivers • Unlimited rides</p>
                </div>
              </div>
              <Badge className="bg-white/20 text-white border-0">
                Active
              </Badge>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="hidden lg:flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Dispatch Center</h1>
            <p className="text-slate-600">Manage ride requests and schedules</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => setShowNotifications(true)}
              data-testid="employer-notifications-button"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">New Requests</p>
                  <p className="text-3xl font-heading font-bold text-orange-600">{newRequests.length}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">To Schedule</p>
                  <p className="text-3xl font-heading font-bold text-blue-600">{approvedRides.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Active Rides</p>
                  <p className="text-3xl font-heading font-bold text-purple-600">{activeRides.length + scheduledRides.length}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Activity className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Drivers</p>
                  <p className="text-3xl font-heading font-bold text-indigo-600">{drivers.length}</p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile Tabs */}
        <div className="lg:hidden mb-4 mt-14">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full h-auto">
              <TabsTrigger value="requests" className="relative py-3 text-base font-bold">
                Requests
                {newRequests.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                    {newRequests.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="livemap" className="py-3 text-base font-bold">Live</TabsTrigger>
              <TabsTrigger value="schedule" className="py-3 text-base font-bold">Schedule</TabsTrigger>
              <TabsTrigger value="drivers" className="py-3 text-base font-bold">Drivers</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Live Map Section */}
        {activeTab === "livemap" && (
          <div className="mb-6" data-testid="live-map-section">
            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Map className="w-6 h-6 text-cyan-500" />
              Live Fleet Tracking
              <Badge className="bg-green-500 text-white animate-pulse">LIVE</Badge>
            </h2>
            
            {/* Interactive Fleet Map */}
            <Card className="border-0 shadow-lg overflow-hidden mb-4">
              <LiveFleetMap 
                mode="dispatch"
                drivers={drivers}
                height="400px"
                variant="light"
                showControls={true}
                onRefresh={fetchDrivers}
                onDriverSelect={(driver) => {
                  toast.info(`Selected: ${driver.name}`);
                }}
              />
            </Card>
            
            {/* Fleet List with Quick Actions */}
            <LiveVehicleMap 
              onAssignRide={handleQuickSchedule}
              onReassignRide={handleReassignRide}
            />
          </div>
        )}

        {/* Today's Rides & unassigned warnings */}
        {(activeTab === "requests" || activeTab === "calendar" || !activeTab) && (
          <TodayRidesPanel onOpenRide={setCalendarRideId} onNewBooking={() => setShowPhoneBooking(true)} refreshKey={calRefresh} />
        )}

        {/* Dispatch Calendar */}
        {activeTab === "calendar" && (
          <div className="space-y-6 mb-6" data-testid="dispatch-calendar-section">
            <CalendarView variant="light" defaultView="week" onOpenRide={setCalendarRideId} refreshKey={calRefresh} testId="dispatch-calendar" />
            <ChangeLogCard refreshKey={calRefresh} onOpenRide={setCalendarRideId} />
          </div>
        )}

        {/* New Requests Section */}
        {(activeTab === "requests" || !activeTab) && (
          <Card className="border-slate-200 mb-6" data-testid="new-requests-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-600" />
                New Ride Requests
                {newRequests.length > 0 && (
                  <Badge className="bg-orange-100 text-orange-800">{newRequests.length} pending</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {newRequests.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No pending requests</p>
              ) : (
                <div className="space-y-4">
                  {newRequests.map((ride) => {
                    const VehicleIcon = vehicleIcons[ride.vehicle_type] || Car;
                    return (
                      <div 
                        key={ride.id}
                        className="p-4 border border-orange-200 bg-orange-50 rounded-xl"
                        data-testid={`request-${ride.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                              <VehicleIcon className="w-5 h-5 text-indigo-900" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{ride.client_name}</p>
                              <p className="text-slate-600 text-sm flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {ride.client_phone}
                              </p>
                            </div>
                          </div>
                          <Badge className="bg-orange-100 text-orange-800">New Request</Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Pickup</p>
                            <p className="text-slate-900 text-sm">{ride.pickup_locations[0]?.address}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Dropoff</p>
                            <p className="text-slate-900 text-sm">{ride.dropoff_address}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 mb-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" /> {ride.pickup_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" /> {ride.pickup_time}
                          </span>
                        </div>

                        {ride.special_requests && (
                          <p className="text-slate-600 text-sm bg-white p-2 rounded mb-3">
                            Note: {ride.special_requests}
                          </p>
                        )}
                        
                        {/* Ride Sharing Preference & Accessibility */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {ride.ride_sharing_preference && (
                            <Badge className={`${
                              ride.ride_sharing_preference === 'shared' 
                                ? 'bg-green-100 text-green-700 border-green-300' 
                                : 'bg-rose-100 text-rose-700 border-rose-300'
                            } border`}>
                              {ride.ride_sharing_preference === 'shared' ? '👥 OK to Share Ride' : '👤 Prefers Alone'}
                            </Badge>
                          )}
                          {ride.mobility_aids?.length > 0 && ride.mobility_aids.map((aid, i) => (
                            <Badge key={i} className="bg-amber-100 text-amber-700 border border-amber-300">
                              {aid === 'wheelchair' ? '♿ Wheelchair' : 
                               aid === 'rolling_walker' ? '🚶 Walker' : 
                               aid === 'cane' ? '🦯 Cane' : aid}
                            </Badge>
                          ))}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveRide(ride.id)}
                            disabled={isLoading}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            data-testid={`approve-${ride.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            onClick={() => handleRejectRide(ride.id)}
                            disabled={isLoading}
                            variant="outline"
                            className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`reject-${ride.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Decline
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Approved - Need Driver Assignment */}
        {(activeTab === "requests" || activeTab === "schedule") && approvedRides.length > 0 && (
          <Card className="border-2 border-blue-300 mb-6 shadow-lg" data-testid="approved-rides-card">
            <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-cyan-50">
              <CardTitle className="font-heading text-xl flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <span className="font-black text-blue-800">Approved - Assign Driver</span>
                <Badge className="bg-blue-500 text-white text-base px-3 py-1">{approvedRides.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                {approvedRides.map((ride) => {
                  const VehicleIcon = vehicleIcons[ride.vehicle_type] || Car;
                  return (
                    <div 
                      key={ride.id}
                      className="p-5 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white rounded-xl shadow-sm"
                      data-testid={`approved-${ride.id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="font-black text-lg text-slate-900">{ride.client_name}</p>
                          <p className="text-slate-600 font-semibold">{ride.pickup_date} at {ride.pickup_time}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                          <VehicleIcon className="w-7 h-7 text-blue-600" />
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-blue-600 text-sm font-bold mb-1 uppercase tracking-wide">Route</p>
                        <p className="text-base font-medium">{ride.pickup_locations[0]?.address} → {ride.dropoff_address}</p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Select value="" onValueChange={(value) => handleAssignDriver(ride.id, value)}>
                          <SelectTrigger className="flex-1 h-12 text-base font-semibold border-2 border-blue-300 bg-white" data-testid={`assign-driver-${ride.id}`}>
                            <SelectValue placeholder="Select driver to assign" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((driver) => {
                              const isOnRide = rides.some(r => 
                                r.driver_id === driver.id && 
                                ["scheduled", "in_progress", "arrived"].includes(r.status)
                              );
                              return (
                                <SelectItem 
                                  key={driver.id} 
                                  value={driver.id}
                                  className="text-base font-medium"
                                >
                                  {driver.name} {isOnRide ? "(has scheduled rides)" : ""}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scheduled & Active Rides */}
        {activeTab === "schedule" && (
          <Card className="border-slate-200 mb-6" data-testid="scheduled-rides-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Today's Schedule</CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={downloadCSVTemplate}
                  data-testid="download-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Template
                </Button>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                    data-testid="csv-upload-input"
                  />
                  <Button 
                    size="sm" 
                    className="bg-gradient-to-r from-purple-500 to-indigo-600 pointer-events-none"
                    asChild
                  >
                    <span><FileUp className="w-4 h-4 mr-2" /> Batch Import</span>
                  </Button>
                </label>
              </div>
            </CardHeader>
            <CardContent>
              {[...scheduledRides, ...activeRides].length === 0 ? (
                <p className="text-slate-500 text-center py-8">No scheduled rides</p>
              ) : (
                <div className="space-y-3">
                  {[...scheduledRides, ...activeRides].map((ride) => {
                    const statusInfo = getStatusInfo(ride.status);
                    const VehicleIcon = vehicleIcons[ride.vehicle_type] || Car;
                    return (
                      <div 
                        key={ride.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                        onClick={() => navigate(`/ride/${ride.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <VehicleIcon className="w-5 h-5 text-indigo-600" />
                          <div>
                            <p className="font-medium text-slate-900">{ride.client_name}</p>
                            <p className="text-slate-500 text-sm">{ride.pickup_time} - {ride.driver_name}</p>
                          </div>
                        </div>
                        <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Drivers Section */}
        {activeTab === "drivers" && (
          <Card className="border-slate-200" data-testid="drivers-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Drivers</CardTitle>
              <Button 
                size="sm" 
                onClick={() => setShowAddMember('driver')}
                className="bg-gradient-to-r from-cyan-500 to-blue-600"
                data-testid="add-driver-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Add Driver
              </Button>
            </CardHeader>
            <CardContent>
              {drivers.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No drivers registered</p>
              ) : (
                <div className="space-y-3">
                  {drivers.map((driver) => {
                    const activeRide = rides.find(r => 
                      r.driver_id === driver.id && 
                      ["scheduled", "in_progress", "arrived"].includes(r.status)
                    );
                    return (
                      <div 
                        key={driver.id}
                        className="p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                              <User className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{driver.name}</p>
                              <p className="text-slate-500 text-sm">{driver.phone || "No phone"}</p>
                              {driver.vehicle_make && (
                                <p className="text-slate-400 text-xs">{driver.vehicle_color} {driver.vehicle_make} {driver.vehicle_model}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={activeRide ? "bg-purple-100 text-purple-800" : "bg-green-100 text-green-800"}>
                              {activeRide ? "On Ride" : "Available"}
                            </Badge>
                            {activeRide && (
                              <p className="text-slate-500 text-xs mt-1">{activeRide.client_name}</p>
                            )}
                            {user?.organization_id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setResetMember({ id: driver.id, name: driver.name })}
                                className="mt-1 h-7 px-2 text-xs text-slate-500 hover:text-indigo-700"
                                data-testid={`driver-reset-link-btn-${driver.id}`}
                              >
                                <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset link
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Show client needs for active ride - human readable */}
                        {activeRide && (activeRide.mobility_aids?.length > 0 || activeRide.assistance_needs?.length > 0) && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <p className="text-sm text-amber-700 flex items-center gap-1">
                              <Accessibility className="w-4 h-4" />
                              <span className="font-medium">Client Has:</span>
                              <span className="text-slate-700">
                                {[
                                  ...(activeRide.mobility_aids?.map(aid => {
                                    const labels = {
                                      'wheelchair': 'Wheelchair',
                                      'rolling_walker': 'Rolling Walker',
                                      'cane': 'Cane',
                                      'crutches': 'Crutches'
                                    };
                                    return labels[aid] || aid.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                                  }) || []),
                                  ...(activeRide.assistance_needs?.map(need => {
                                    const labels = {
                                      'step_stool': 'Needs Step Stool',
                                      'entry_exit_help': 'Needs Help In/Out of Vehicle',
                                      'blind': 'Visually Impaired',
                                      'deaf': 'Hearing Impaired',
                                      'oxygen': 'Uses Oxygen',
                                      'service_animal': 'Has Service Animal'
                                    };
                                    return labels[need] || need.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                                  }) || [])
                                ].join(', ')}
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Clients Tab */}
        {activeTab === "clients" && (
          <Card className="border-slate-200" data-testid="clients-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Clients (HIPAA Secure)
              </CardTitle>
              <Button 
                size="sm" 
                onClick={() => setShowAddMember('client')}
                className="bg-gradient-to-r from-emerald-500 to-teal-600"
                data-testid="add-client-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Add Client
              </Button>
            </CardHeader>
            <CardContent>
              {clients.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No clients added yet</p>
                  <p className="text-slate-400 text-sm mt-1">Add clients to schedule their rides</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {clients.map((client) => (
                    <div 
                      key={client.id}
                      className="p-4 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{client.name}</p>
                            <p className="text-slate-500 text-sm">{client.phone || client.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {user?.organization_id && (client.source === 'manual' ? client.id : client.user_id) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setResetMember({ id: client.source === 'manual' ? client.id : client.user_id, name: client.name })}
                              className="h-7 px-2 text-xs text-slate-500 hover:text-indigo-700"
                              data-testid={`client-reset-link-btn-${client.id}`}
                            >
                              <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset link
                            </Button>
                          )}
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        </div>
                      </div>
                      {(client.mobility_aids?.length > 0 || client.assistance_needs?.length > 0) && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500 font-medium">Accessibility Needs:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {client.mobility_aids?.map(aid => (
                              <Badge key={aid} variant="outline" className="text-xs">{aid.replace(/_/g, ' ')}</Badge>
                            ))}
                            {client.assistance_needs?.map(need => (
                              <Badge key={need} variant="outline" className="text-xs bg-blue-50">{need.replace(/_/g, ' ')}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Trip Logs Tab */}
        {activeTab === "tripLogs" && (
          <Card className="border-slate-200" data-testid="trip-logs-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-blue-600" />
                Trip Logs
              </CardTitle>
              <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {tripLogs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No trip logs yet</p>
                  <p className="text-slate-400 text-sm mt-1">Completed rides will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 font-medium text-slate-600">Date</th>
                        <th className="text-left py-3 px-2 font-medium text-slate-600">Client</th>
                        <th className="text-left py-3 px-2 font-medium text-slate-600">Driver</th>
                        <th className="text-left py-3 px-2 font-medium text-slate-600">Pickup</th>
                        <th className="text-left py-3 px-2 font-medium text-slate-600">Dropoff</th>
                        <th className="text-right py-3 px-2 font-medium text-slate-600">
                          {unitSystem === 'imperial' ? 'Miles' : 'Km'}
                        </th>
                        <th className="text-right py-3 px-2 font-medium text-slate-600">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tripLogs.slice(0, 20).map((log) => {
                        const dist = convertDistance(parseFloat(log.mileage) || 0);
                        return (
                          <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-2 text-slate-900">{log.trip_date}</td>
                            <td className="py-3 px-2 text-slate-700">{log.client_name}</td>
                            <td className="py-3 px-2 text-slate-700">{log.driver_name}</td>
                            <td className="py-3 px-2 text-slate-500 truncate max-w-32">{log.pickup_address}</td>
                            <td className="py-3 px-2 text-slate-500 truncate max-w-32">{log.dropoff_address}</td>
                            <td className="py-3 px-2 text-right text-slate-700">{dist.value} {dist.unit}</td>
                            <td className="py-3 px-2 text-right font-medium text-slate-900">${log.cost?.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Manifest Tab */}
        {activeTab === "manifest" && (
          <Card className="border-slate-200" data-testid="manifest-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Daily Manifest
              </CardTitle>
              <div className="flex items-center gap-2">
                <Input 
                  type="date" 
                  value={manifestDate}
                  onChange={(e) => {
                    setManifestDate(e.target.value);
                    fetchManifest(e.target.value);
                  }}
                  className="w-40"
                />
                <Button variant="outline" size="sm" onClick={handlePrintManifest} data-testid="print-manifest-btn">
                  <Download className="w-4 h-4 mr-2" /> Print
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{manifest?.total_rides || 0}</p>
                  <p className="text-sm text-blue-700">Total Rides</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{manifest?.completed || 0}</p>
                  <p className="text-sm text-green-700">Completed</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">{manifest?.scheduled || 0}</p>
                  <p className="text-sm text-amber-700">Scheduled</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{manifest?.in_progress || 0}</p>
                  <p className="text-sm text-purple-700">In Progress</p>
                </div>
              </div>
              
              {manifest?.rides?.length > 0 ? (
                <div className="space-y-3">
                  {manifest.rides.map((ride) => (
                    <div key={ride.id} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Badge className={
                            ride.status === 'completed' ? 'bg-green-100 text-green-800' :
                            ride.status === 'in_progress' ? 'bg-purple-100 text-purple-800' :
                            'bg-amber-100 text-amber-800'
                          }>
                            {ride.status}
                          </Badge>
                          <span className="font-medium text-slate-900">{ride.client_name}</span>
                        </div>
                        <span className="text-slate-500 text-sm">{ride.pickup_time}</span>
                      </div>
                      <div className="text-sm text-slate-600">
                        <p><span className="text-slate-400">From:</span> {ride.pickup_locations?.[0]?.address}</p>
                        <p><span className="text-slate-400">To:</span> {ride.dropoff_address}</p>
                        {ride.driver_name && (
                          <p className="mt-1"><span className="text-slate-400">Driver:</span> {ride.driver_name}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No rides scheduled for this date</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Claims & Reimbursement Tab */}
        {activeTab === "claims" && (
          <div className="space-y-6">
            {/* Insurance Claims Manager */}
            <Card className="border-slate-200" data-testid="claims-card">
              <CardContent className="pt-6">
                <InsuranceClaimsManager 
                  completedRides={rides.filter(r => r.status === 'completed')}
                />
              </CardContent>
            </Card>
            
            {/* Recurring Rides Manager */}
            <Card className="border-slate-200" data-testid="recurring-rides-card">
              <CardContent className="pt-6">
                <RecurringRidesManager 
                  clients={clients}
                  drivers={drivers}
                  onRidesGenerated={fetchRides}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Driver Leaderboard Tab */}
        {activeTab === "leaderboard" && (
          <div className="space-y-6">
            <Card className="border-slate-200" data-testid="leaderboard-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Driver Leaderboard
                </CardTitle>
                <Button size="sm" onClick={fetchLeaderboard} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-12">
                    <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No driver data available</p>
                    <Button onClick={fetchLeaderboard} className="mt-4">Load Leaderboard</Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {leaderboard.map((driver, index) => (
                      <div 
                        key={driver.id}
                        className={`p-4 rounded-xl border ${
                          index === 0 ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300' :
                          index === 1 ? 'bg-slate-50 border-slate-300' :
                          index === 2 ? 'bg-orange-50 border-orange-200' :
                          'bg-white border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                              index === 0 ? 'bg-amber-400 text-white' :
                              index === 1 ? 'bg-slate-400 text-white' :
                              index === 2 ? 'bg-orange-400 text-white' :
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {index + 1}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-slate-900">{driver.name}</p>
                                {driver.driver_of_month && (
                                  <Badge className="bg-amber-100 text-amber-800 text-xs">
                                    <Crown className="w-3 h-3 mr-1" /> Driver of the Month
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-sm text-slate-500">{driver.completed_rides} rides</span>
                                <span className="text-sm text-green-600">{driver.on_time_percent}% on-time</span>
                                <span className="text-sm text-amber-500 flex items-center">
                                  <Star className="w-3 h-3 mr-1 fill-amber-400" /> {driver.rating}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                            {driver.badges?.map((badge, i) => (
                              <span key={i} className="text-xl" title={badge.description}>{badge.emoji}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Available Badges Info */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-500" />
                  Available Badges
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { emoji: "💯", name: "Century Club", desc: "100 rides" },
                    { emoji: "🏆", name: "Road Warrior", desc: "500 rides" },
                    { emoji: "👑", name: "Legend", desc: "1000 rides" },
                    { emoji: "⭐", name: "Perfect Week", desc: "100% on-time" },
                    { emoji: "♿", name: "Accessibility Champion", desc: "50+ wheelchair rides" },
                    { emoji: "🌅", name: "Early Bird", desc: "50+ morning rides" },
                    { emoji: "🦉", name: "Night Owl", desc: "50+ evening rides" },
                    { emoji: "⭐", name: "Five Star", desc: "5.0 rating" },
                  ].map((badge, i) => (
                    <div key={i} className="text-center p-3 bg-slate-50 rounded-lg">
                      <span className="text-2xl">{badge.emoji}</span>
                      <p className="font-medium text-slate-900 text-sm mt-1">{badge.name}</p>
                      <p className="text-slate-500 text-xs">{badge.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reminders Tab */}
        {activeTab === "reminders" && (
          <div className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <ReminderSettingsCard onSaved={() => setCalRefresh((v) => v + 1)} />
              <ReminderStatusCard refreshKey={calRefresh} />
            </div>
            <Card className="border-slate-200" data-testid="reminders-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-500" />
                  Appointment Reminders
                </CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" onClick={fetchReminders} variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                  <Button size="sm" onClick={handleSendAllReminders} className="bg-blue-500 hover:bg-blue-600">
                    <Send className="w-4 h-4 mr-2" /> Send All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {reminders.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No upcoming rides need reminders</p>
                    <Button onClick={fetchReminders} className="mt-4">Check for Reminders</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reminders.map((reminder) => (
                      <div 
                        key={reminder.ride_id}
                        className={`p-4 rounded-xl border ${
                          reminder.reminder_type === 'same_day' 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-blue-50 border-blue-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-slate-900">{reminder.client_name}</p>
                              <Badge className={
                                reminder.reminder_type === 'same_day' 
                                  ? 'bg-amber-100 text-amber-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }>
                                {reminder.reminder_type === 'same_day' ? 'Today' : 'Tomorrow'}
                              </Badge>
                            </div>
                            <p className="text-slate-600 text-sm mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {reminder.pickup_date} at {reminder.pickup_time}
                            </p>
                            <p className="text-slate-500 text-sm">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {reminder.dropoff_address}
                            </p>
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => handleSendReminder(reminder.ride_id)}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            <Send className="w-4 h-4 mr-1" /> Send
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Smart Scheduling / Predictions Tab */}
        {activeTab === "predictions" && (
          <div className="space-y-6">
            <Card className="border-slate-200" data-testid="predictions-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-500" />
                  Smart Scheduling Predictions
                </CardTitle>
                <Button size="sm" onClick={fetchPredictions} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" /> Analyze Patterns
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-slate-500 mb-4">
                  AI-powered analysis of ride patterns to suggest recurring schedules.
                </p>
                
                {predictions.length === 0 ? (
                  <div className="text-center py-12">
                    <Brain className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No patterns detected yet</p>
                    <p className="text-slate-400 text-sm">Complete more rides to see scheduling suggestions</p>
                    <Button onClick={fetchPredictions} className="mt-4">Run Analysis</Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {predictions.map((prediction, index) => (
                      <div 
                        key={index}
                        className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-semibold text-slate-900">{prediction.client_name}</p>
                              <Badge className="bg-purple-100 text-purple-800">
                                <Target className="w-3 h-3 mr-1" /> {prediction.confidence}% confidence
                              </Badge>
                            </div>
                            <p className="text-slate-700 text-sm mb-2">
                              <Sparkles className="w-4 h-4 inline mr-1 text-purple-500" />
                              {prediction.pattern_description}
                            </p>
                            <div className="flex flex-wrap gap-2 text-sm text-slate-600">
                              <span className="bg-white px-2 py-1 rounded border">
                                <MapPin className="w-3 h-3 inline mr-1" />
                                {prediction.suggested_destination?.slice(0, 40)}...
                              </span>
                              <span className="bg-white px-2 py-1 rounded border">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                {prediction.suggested_days?.join(', ')}
                              </span>
                              <span className="bg-white px-2 py-1 rounded border">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {prediction.suggested_time}
                              </span>
                            </div>
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => handleCreateRecurring(prediction)}
                            className="bg-purple-500 hover:bg-purple-600 ml-4"
                          >
                            <Plus className="w-4 h-4 mr-1" /> Create Schedule
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Live Map */}
        {activeTab === "schedule" && activeRides.length > 0 && (
          <Card className="border-slate-200 mt-6" data-testid="live-map-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">Live Tracking</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 rounded-xl overflow-hidden">
                <MockMap 
                  rides={activeRides}
                  showMultiple={true}
                  variant="light"
                />
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <ResetLinkDialog member={resetMember} onClose={() => setResetMember(null)} />

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddMember(null)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Add New {showAddMember === 'driver' ? 'Driver' : 'Client'}
            </h3>
            
            {showAddMember === 'driver' && (
              <p className="text-sm text-slate-500 mb-4">
                Set a starter password and tell the driver in person. They'll choose their own password the first time they sign in.
              </p>
            )}
            
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={newMember.name}
                  onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label>Email * {showAddMember === 'driver' && <span className="text-slate-400 font-normal">(used for login)</span>}</Label>
                <Input 
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
              
              {showAddMember === 'driver' && (
                <div>
                  <Label>Starter password * <span className="text-slate-400 font-normal">(for driver login)</span></Label>
                  <PasswordInput 
                    value={newMember.password || ''}
                    onChange={(e) => setNewMember({...newMember, password: e.target.value})}
                    placeholder="At least 6 characters"
                    data-testid="add-member-password-input"
                    toggleClassName="text-slate-500 hover:text-slate-800"
                  />
                  <p className="text-xs text-slate-400 mt-1">Tell the driver in person. It is never sent by text, and they must choose their own password at first sign-in.</p>
                </div>
              )}
              
              <div>
                <Label>Phone</Label>
                <Input 
                  value={newMember.phone}
                  onChange={(e) => setNewMember({...newMember, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              
              {showAddMember === 'driver' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Vehicle Make</Label>
                      <Input 
                        value={newMember.vehicle_make}
                        onChange={(e) => setNewMember({...newMember, vehicle_make: e.target.value})}
                        placeholder="Toyota"
                      />
                    </div>
                    <div>
                      <Label>Model</Label>
                      <Input 
                        value={newMember.vehicle_model}
                        onChange={(e) => setNewMember({...newMember, vehicle_model: e.target.value})}
                        placeholder="Camry"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Color</Label>
                      <Input 
                        value={newMember.vehicle_color}
                        onChange={(e) => setNewMember({...newMember, vehicle_color: e.target.value})}
                        placeholder="White"
                      />
                    </div>
                    <div>
                      <Label>Plate</Label>
                      <Input 
                        value={newMember.vehicle_plate}
                        onChange={(e) => setNewMember({...newMember, vehicle_plate: e.target.value})}
                        placeholder="ABC-1234"
                      />
                    </div>
                  </div>
                </>
              )}
              
              {showAddMember === 'client' && (
                <>
                  <div>
                    <Label>Starter password <span className="text-slate-400 font-normal">(optional, only if they will sign in)</span></Label>
                    <PasswordInput 
                      value={newMember.password || ''}
                      onChange={(e) => setNewMember({...newMember, password: e.target.value})}
                      placeholder="Leave blank if they won't sign in"
                      data-testid="add-client-password-input"
                      toggleClassName="text-slate-500 hover:text-slate-800"
                    />
                    <p className="text-xs text-slate-400 mt-1">Never sent by text. They must choose their own password at first sign-in. You can also send a reset link later.</p>
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input 
                      value={newMember.address}
                      onChange={(e) => setNewMember({...newMember, address: e.target.value})}
                      placeholder="Home address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Emergency Contact</Label>
                      <Input 
                        value={newMember.emergency_contact}
                        onChange={(e) => setNewMember({...newMember, emergency_contact: e.target.value})}
                        placeholder="Contact name"
                      />
                    </div>
                    <div>
                      <Label>Emergency Phone</Label>
                      <Input 
                        value={newMember.emergency_phone}
                        onChange={(e) => setNewMember({...newMember, emergency_phone: e.target.value})}
                        placeholder="(555) 987-6543"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Medical Notes</Label>
                    <Input 
                      value={newMember.medical_notes}
                      onChange={(e) => setNewMember({...newMember, medical_notes: e.target.value})}
                      placeholder="Any medical information"
                    />
                  </div>
                  
                  {/* Accessibility Needs Section */}
                  <div className="pt-2 border-t border-slate-200">
                    <Label className="flex items-center gap-2 text-amber-700">
                      <Accessibility className="w-4 h-4" /> Accessibility Needs
                    </Label>
                    <div className="mt-2 space-y-3">
                      <div>
                        <p className="text-xs text-slate-600 mb-2 font-medium">Client has mobility aid (choose all that apply)</p>
                        <div className="flex flex-wrap gap-2">
                          {['wheelchair', 'rolling_walker', 'cane', 'crutches'].map(aid => (
                            <button
                              key={aid}
                              type="button"
                              onClick={() => {
                                const aids = newMember.mobility_aids || [];
                                if (aids.includes(aid)) {
                                  setNewMember({...newMember, mobility_aids: aids.filter(a => a !== aid)});
                                } else {
                                  setNewMember({...newMember, mobility_aids: [...aids, aid]});
                                }
                              }}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                                newMember.mobility_aids?.includes(aid) 
                                  ? 'bg-amber-500 text-white' 
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {aid.replace(/_/g, ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-slate-600 mb-2 font-medium">Client needs assistance (choose all that apply)</p>
                        <div className="flex flex-wrap gap-2">
                          {['step_stool', 'entry_exit_help', 'blind', 'deaf', 'oxygen', 'service_animal'].map(need => (
                            <button
                              key={need}
                              type="button"
                              onClick={() => {
                                const needs = newMember.assistance_needs || [];
                                if (needs.includes(need)) {
                                  setNewMember({...newMember, assistance_needs: needs.filter(n => n !== need)});
                                } else {
                                  setNewMember({...newMember, assistance_needs: [...needs, need]});
                                }
                              }}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                                newMember.assistance_needs?.includes(need) 
                                  ? 'bg-blue-500 text-white' 
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {need.replace(/_/g, ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Ride Sharing Preference */}
                      <div>
                        <p className="text-xs text-slate-600 mb-2 font-medium">Ride Sharing Preference</p>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setNewMember({...newMember, ride_sharing_preference: 'alone'})}
                            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              newMember.ride_sharing_preference === 'alone' || !newMember.ride_sharing_preference
                                ? 'bg-rose-500 text-white shadow-lg' 
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            <User className="w-4 h-4 inline mr-2" />
                            Prefers Alone
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewMember({...newMember, ride_sharing_preference: 'shared'})}
                            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              newMember.ride_sharing_preference === 'shared'
                                ? 'bg-green-500 text-white shadow-lg' 
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            <Users className="w-4 h-4 inline mr-2" />
                            OK to Share
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Preferred Driver */}
                  <div className="pt-2">
                    <Label>Preferred Driver (Optional)</Label>
                    <Select 
                      value={newMember.preferred_driver || 'none'}
                      onValueChange={(value) => setNewMember({...newMember, preferred_driver: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="No preference" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No preference</SelectItem>
                        {drivers.map(driver => (
                          <SelectItem key={driver.id} value={driver.id}>
                            {driver.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAddMember(null)}
              >
                Cancel
              </Button>
              <Button 
                className={`flex-1 ${showAddMember === 'driver' ? 'bg-gradient-to-r from-cyan-500 to-blue-600' : 'bg-gradient-to-r from-emerald-500 to-teal-600'}`}
                onClick={() => handleAddMember(showAddMember)}
                data-testid="add-member-submit-btn"
                disabled={isLoading || !newMember.name || !newMember.email}
              >
                {isLoading ? 'Adding...' : `Add ${showAddMember === 'driver' ? 'Driver' : 'Client'}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Import Modal */}
      {showBatchImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowBatchImport(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-2xl shadow-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <FileUp className="w-6 h-6 text-purple-600" />
              Batch Import Preview
            </h3>
            
            {batchImportErrors.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4" /> Import Errors
                </p>
                <ul className="text-red-700 text-sm list-disc list-inside">
                  {batchImportErrors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                {batchImportData.length} rides ready to import
              </p>
            </div>
            
            {batchImportData.length > 0 && (
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-2 font-medium text-slate-600">Client</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-600">Phone</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-600">Date</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-600">Time</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-600">Accessibility</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchImportData.slice(0, 10).map((ride, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2 px-2 text-slate-900">{ride.client_name}</td>
                        <td className="py-2 px-2 text-slate-600">{ride.client_phone}</td>
                        <td className="py-2 px-2 text-slate-600">{ride.pickup_date}</td>
                        <td className="py-2 px-2 text-slate-600">{ride.pickup_time}</td>
                        <td className="py-2 px-2">
                          <div className="flex flex-wrap gap-1">
                            {ride.mobility_aids?.map(aid => (
                              <Badge key={aid} className="bg-amber-100 text-amber-800 text-xs">{aid}</Badge>
                            ))}
                            {ride.assistance_needs?.map(need => (
                              <Badge key={need} className="bg-blue-100 text-blue-800 text-xs">{need}</Badge>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {batchImportData.length > 10 && (
                  <p className="text-slate-500 text-sm mt-2 text-center">
                    ...and {batchImportData.length - 10} more rides
                  </p>
                )}
              </div>
            )}
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowBatchImport(false);
                  setBatchImportData([]);
                  setBatchImportErrors([]);
                }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600"
                onClick={handleBatchSubmit}
                disabled={isLoading || batchImportData.length === 0}
              >
                {isLoading ? 'Importing...' : `Import ${batchImportData.length} Rides`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Panel */}
      <NotificationPanel 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)} 
      />

      <RideDetailDialog rideId={calendarRideId} onClose={() => setCalendarRideId(null)} mode="dispatch" drivers={drivers} onChanged={refreshSchedule} />
      <PhoneBookingDialog open={showPhoneBooking} onClose={() => setShowPhoneBooking(false)} drivers={drivers} onCreated={refreshSchedule} />
      {conflictDialog}

      {/* SMS Log Panel */}
      <SMSLogPanel 
        isOpen={showSMSLog} 
        onClose={() => setShowSMSLog(false)} 
      />
    </div>
  );
}
